/*
  Ministry Checkup results dashboard. Ordinal categorical scoring model.
*/
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import { get } from '../api/client';

// Ordinal categorical scoring: Yes=3, Needs Improvement=2, No=1, N/A=excluded
const computeQuestionScore = (answers) => {
  const Y = answers.filter((a) => a.answer === 'Yes').length;
  const NI = answers.filter((a) => a.answer === 'Needs Improvement').length;
  const N = answers.filter((a) => a.answer === 'No').length;
  const NA = answers.filter((a) => a.answer === 'N/A' || a.answer === 'Not Observed').length;
  const scoredResponses = Y + NI + N;
  if (scoredResponses === 0) return { healthScorePercent: null, status: 'No data', Y, NI, N, NA, scoredResponses };
  const weightedPoints = Y * 3 + NI * 2 + N * 1;
  const maxPoints = scoredResponses * 3;
  const healthScorePercent = Math.round((weightedPoints / maxPoints) * 1000) / 10;
  let status = healthScorePercent >= 90 ? 'Strong' : healthScorePercent >= 75 ? 'Room to Improve' : 'Needs Attention';
  const noRate = (N / scoredResponses) * 100;
  const needsImprovementRate = (NI / scoredResponses) * 100;
  if (noRate >= 15) status = 'Needs Attention';
  else if (needsImprovementRate >= 30) status = 'Room to Improve';
  return { healthScorePercent, status, Y, NI, N, NA, scoredResponses };
};

const STATUS_COLORS = { Strong: '#15803d', 'Room to Improve': '#a16207', 'Needs Attention': '#b91c1c', 'No data': '#6b7280' };

const MinistryCheckupResultsPage = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState(new Set());

  const toggleSection = (key) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  useEffect(() => {
    const fetch = async () => {
      const { ok, data: d } = await get(`/api/surveys/${id}/results`);
      if (ok) setData(d);
      setLoading(false);
    };
    if (id) fetch();
  }, [id]);

  if (loading) return <div style={{ padding: '2rem' }}>Loading…</div>;
  if (!data) return <div style={{ padding: '2rem' }}>Not found.</div>;

  const { survey, responses, template } = data;
  const submitted = responses.filter((r) => r.status === 'submitted');

  // Build section scores (aggregate Y, NI, N across all questions in section)
  const sectionScores = {};
  (template.sections || []).forEach((sec) => {
    let Y = 0, NI = 0, N = 0;
    submitted.forEach((r) => {
      (r.responses || []).forEach((resp) => {
        const q = (sec.questions || []).find((qu) => qu.key === resp.questionKey);
        if (q && resp.answer && resp.answer !== 'N/A' && resp.answer !== 'Not Observed') {
          if (resp.answer === 'Yes') Y += 1;
          else if (resp.answer === 'Needs Improvement') NI += 1;
          else if (resp.answer === 'No') N += 1;
        }
      });
    });
    const scoredResponses = Y + NI + N;
    let healthScorePercent = null;
    let status = 'No data';
    if (scoredResponses > 0) {
      const weightedPoints = Y * 3 + NI * 2 + N * 1;
      const maxPoints = scoredResponses * 3;
      healthScorePercent = Math.round((weightedPoints / maxPoints) * 1000) / 10;
      status = healthScorePercent >= 90 ? 'Strong' : healthScorePercent >= 75 ? 'Room to Improve' : 'Needs Attention';
      const noRate = (N / scoredResponses) * 100;
      const needsImprovementRate = (NI / scoredResponses) * 100;
      if (noRate >= 15) status = 'Needs Attention';
      else if (needsImprovementRate >= 30) status = 'Room to Improve';
    }
    sectionScores[sec.key] = { healthScorePercent, status, Y, NI, N, scoredResponses };
  });

  return (
    <div style={{ maxWidth: '1200px', width: '100%', margin: '0 auto', padding: '0 1rem 6rem 1rem', boxSizing: 'border-box' }}>
      <PageHeader title={`Survey Results: ${survey?.title || ''}`} backTo="/ministry-checkup" backLabel="← Ministry Checkup" />

      <p style={{ color: 'var(--cr-text-muted)', fontSize: '0.95rem', marginBottom: '0.5rem' }}>
        {submitted.length} response{submitted.length !== 1 ? 's' : ''} submitted.
        <span style={{ marginLeft: '0.5rem' }}>
          <span style={{ color: '#22c55e', fontWeight: 500 }}>Strong</span> ·{' '}
          <span style={{ color: '#eab308', fontWeight: 500 }}>Room to Improve</span> ·{' '}
          <span style={{ color: '#ef4444', fontWeight: 500 }}>Needs Attention</span>
        </span>
      </p>
      <p style={{ color: 'var(--cr-text-muted)', fontSize: '0.85rem', marginBottom: '1.5rem', lineHeight: 1.5 }}>
        We use an ordinal categorical model: Yes=3, Needs Improvement=2, No=1 (N/A excluded). 
        Health score = (weighted points ÷ max points) × 100. Status bands: 90%+ Strong, 75–90% Room to Improve, &lt;75% Needs Attention. 
        Guardrails: ≥15% No responses → Needs Attention; ≥30% Needs Improvement → Room to Improve.
      </p>

      {submitted.length === 0 ? (
        <p>No responses yet.</p>
      ) : (
        <>
          <h3 style={{ marginBottom: '0.75rem' }}>System health by section</h3>
          <p style={{ fontSize: '0.9rem', color: 'var(--cr-text-muted)', marginBottom: '1rem' }}>
            Click a section to expand and see responses by question.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '2rem' }}>
            {(template.sections || []).map((sec) => {
              const s = sectionScores[sec.key];
              const pct = s?.healthScorePercent ?? 0;
              const color = STATUS_COLORS[s?.status] ?? '#6b7280';
              const isExpanded = expandedSections.has(sec.key);
              return (
                <div
                  key={sec.key}
                  style={{
                    background: 'var(--cr-bg-warm)',
                    borderRadius: '8px',
                    border: '1px solid var(--cr-border)',
                    overflow: 'hidden',
                  }}
                >
                  <button
                    type="button"
                    className="section-expand-btn"
                    onClick={() => toggleSection(sec.key)}
                    style={{
                      width: '100%',
                      padding: '1rem',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      textAlign: 'left',
                      font: 'inherit',
                      color: 'inherit',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ transition: 'transform 0.2s', transform: isExpanded ? 'rotate(90deg)' : 'none' }}>▶</span>
                      <strong>{sec.title}</strong>
                      <span style={{ fontSize: '0.9rem', color: 'var(--cr-text-muted)' }}>
                        {s?.status === 'No data' ? '(No data)' : `${s?.healthScorePercent}% · ${s?.status}`}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <div style={{ width: '80px', height: '8px', background: 'var(--cr-border)', borderRadius: '4px', overflow: 'hidden' }}>
                        <div style={{ width: `${pct || 0}%`, height: '100%', background: color, borderRadius: '4px' }} />
                      </div>
                      <span style={{ fontWeight: 600, color }}>{pct != null ? `${pct}%` : '—'}</span>
                    </div>
                  </button>
                  {isExpanded && (
                    <div style={{ padding: '0 1rem 1rem 1rem', borderTop: '1px solid var(--cr-border)' }}>
                      {(sec.questions || []).map((q) => {
                        const answers = submitted.flatMap((r) => {
                          const resp = (r.responses || []).find((x) => x.questionKey === q.key);
                          return resp?.answer ? [resp] : [];
                        });
                        const score = computeQuestionScore(answers);
                        const qColor = STATUS_COLORS[score.status];
                        const comments = submitted.flatMap((r) => {
                          const resp = (r.responses || []).find((x) => x.questionKey === q.key);
                          return resp?.comment ? [{ user: r.user, comment: resp.comment }] : [];
                        }).filter((c) => c.comment.trim());
                        return (
                          <div key={q.key} style={{ marginTop: '1rem', paddingBottom: '1rem', borderBottom: '1px solid var(--cr-border)' }}>
                            <div style={{ fontWeight: 500, marginBottom: '0.35rem' }}>
                              {q.text}
                              {q.items && (
                                <ul style={{ margin: '0.25rem 0 0 1rem', paddingLeft: '0.5rem', fontWeight: 400 }}>
                                  {q.items.map((item, i) => (
                                    <li key={i} style={{ marginBottom: '0.15rem' }}>{item}</li>
                                  ))}
                                </ul>
                              )}
                            </div>
                            <div style={{ fontSize: '0.9rem', color: 'var(--cr-text-muted)', marginBottom: '0.35rem', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem' }}>
                              {score.healthScorePercent != null ? (
                                <>
                              <span style={{ fontWeight: 600, color: qColor }}>{score.healthScorePercent}% · {score.status}</span>
                              <span>|</span>
                              <span>Yes: {score.Y} · Needs Improvement: {score.NI} · No: {score.N} · N/A: {score.NA} (n={score.scoredResponses})</span>
                                </>
                              ) : (
                                <span>No data · Yes: {score.Y} · Needs Improvement: {score.NI} · No: {score.N} · N/A: {score.NA}</span>
                              )}
                            </div>
                            {q.checklist && q.items && (() => {
                              const itemCounts = (q.items || []).map((_, i) => {
                                let present = 0, absent = 0, na = 0;
                                submitted.forEach((r) => {
                                  const resp = (r.responses || []).find((x) => x.questionKey === q.key);
                                  const v = (resp?.itemAnswers?.[i] || '').toLowerCase();
                                  if (v === 'present') present += 1;
                                  else if (v === 'absent') absent += 1;
                                  else if (v === 'na') na += 1;
                                });
                                return { present, absent, na };
                              });
                              const hasItemData = itemCounts.some((c) => c.present + c.absent + c.na > 0);
                              if (!hasItemData) return null;
                              return (
                                <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: 'var(--cr-text-muted)' }}>
                                  <strong>Per item:</strong>
                                  <ul style={{ margin: '0.25rem 0 0 1rem', paddingLeft: '0.5rem' }}>
                                    {(q.items || []).map((item, i) => {
                                      const c = itemCounts[i] || {};
                                      return (
                                        <li key={i} style={{ marginBottom: '0.2rem' }}>
                                          {item}: {c.present}✓ present · {c.absent} absent · {c.na} N/A
                                        </li>
                                      );
                                    })}
                                  </ul>
                                </div>
                              );
                            })()}
                            {comments.length > 0 && (
                              <div style={{ marginTop: '0.5rem', fontSize: '0.9rem' }}>
                                <strong>Comments:</strong>
                                {comments.map((c, i) => (
                                  <div key={i} style={{ marginTop: '0.25rem', fontStyle: 'italic' }}>
                                    {c.user?.firstName} {c.user?.lastName}: {c.comment}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {template.finalQuestion && (
            <div style={{ marginTop: '1.5rem' }}>
              <h4 style={{ marginBottom: '0.5rem' }}>{template.finalQuestion.text}</h4>
              <div style={{ padding: '1rem', background: 'var(--cr-bg-warm)', borderRadius: '8px', border: '1px solid var(--cr-border)' }}>
                {(() => {
                  const withFinalComment = submitted.filter((r) => r.finalComment?.trim());
                  return withFinalComment.length === 0 ? (
                    <p style={{ color: 'var(--cr-text-muted)' }}>No responses</p>
                  ) : (
                    withFinalComment.map((r) => (
                      <div key={r.userId} style={{ marginBottom: '0.75rem' }}>
                        <strong>{r.user?.firstName} {r.user?.lastName}:</strong>
                        <p style={{ margin: '0.25rem 0 0 0', fontStyle: 'italic' }}>{r.finalComment}</p>
                      </div>
                    ))
                  );
                })()}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MinistryCheckupResultsPage;
